import React, { useState } from 'react';

const PlusIcon = () => <span className="material-icons text-[20px]">add</span>;
const MinusIcon = () => <span className="material-icons text-[20px]">remove</span>;

interface FundingSection {
  id: string;
  title: string;
  subtitle: string;
  tags: string[];
  body: string;
}

const fundingContent: FundingSection[] = [
  {
    id: "funding_readiness_checklist",
    title: "Funding Readiness Checklist",
    subtitle: "Be clear before you apply for any type of funding.",
    tags: ["beginner", "all_industries", "grants", "crowdfunding", "investors"],
    body: `Before you apply for any grant, crowdfunding campaign, or investment, make sure you're clear on:

**1. Your mission**
What you exist to do and why it matters.

**2. Who you support**
Your audience, community, customers, clients, or beneficiaries.

**3. The problem you solve**
Why your work is needed. What would happen if you didn't exist?

**4. What funding will change**
What outcomes will improve if you secure funding.

**5. What makes you credible**
Your experience, skills, lived experience, track record, or team.

ROOTT uses your profile and onboarding answers to auto-fill most of this. Keep your profile complete and up to date so the platform can work harder for you.`
  },
  {
    id: "funding_types_overview",
    title: "Funding Types — Which Is Right for You?",
    subtitle: "Understand your options: grants, crowdfunding, investors, and more.",
    tags: ["beginner", "all_industries"],
    body: `Different types of funding suit different goals. Most organisations use a mix.

**💰 Grants (non-repayable)**
Best for: charities, CICs, community projects, research, creative work, early-stage ideas.
Good when: you are focused on impact and public benefit more than profit.

**📢 Crowdfunding**
Best for: community-led ideas, early validation, building a supporter base, smaller or medium targets.
Good when: your story is relatable and you have people or networks you can reach.
In ROOTT: you get a commission-free campaign page and auto-written social posts.

**📈 Investors (equity or revenue-share)**
Best for: scalable or high-growth businesses, tech products, or ventures with clear revenue potential.
Good when: you are comfortable sharing ownership or future profits.

**🏦 Loans & community finance**
Best for: stable organisations with predictable revenue.
Good when: you need capital for equipment, expansion, or cash flow and can afford repayments.

ROOTT helps you explore these options in one place, so you can decide what mix works best for your mission.`
  },
  {
    id: "five_things_funders_want",
    title: "The Five Things All Funders Want",
    subtitle: "Works across all industries and funding types.",
    tags: ["all_industries", "grants", "crowdfunding", "investors"],
    body: `Regardless of sector, every funder is looking for the same core signals:

**1. Clarity**
Do you explain your work simply and confidently, without jargon?

**2. Need**
Is the problem real and clear? Is there a genuine gap or issue you are tackling?

**3. Impact**
What difference will their money make? Who benefits, and how?

**4. Feasibility**
Can you realistically deliver what you're promising with the time, resources, and team you have?

**5. Value for money**
Is your budget reasonable, transparent, and aligned with your outcomes?

ROOTT's AI Grant Writer uses your profile, project details, and budget to strengthen all five areas automatically.`
  },
  {
    id: "project_summary_framework",
    title: "The Perfect Project Summary",
    subtitle: "A simple structure that works for grants, crowdfunding, and investors.",
    tags: ["all_industries", "grants", "crowdfunding", "investors"],
    body: `Use this structure whenever you summarise your project — in applications, pitches, or campaign pages:

**1. The Problem**
Who is struggling, or what gap exists?

**2. Your Solution**
What you will actually do, in practical terms.

**3. Why You**
Your story, credibility, team, or lived experience.

**4. Who Benefits**
Who will be positively impacted and how.

**5. The Change You Expect**
The outcomes and improvements you want to see.

**6. What You Need**
The funding amount and what it will pay for.

ROOTT can auto-generate this summary from your profile and narrative answers, and then adapt it to different funders.`
  },
  {
    id: "universal_budget_template",
    title: "Universal Budget Template",
    subtitle: "A simple budget layout that any funder can understand.",
    tags: ["all_industries", "grants", "crowdfunding", "investors"],
    body: `Most funders, across all industries, understand budgets best when they are grouped into clear categories:

**1. People**
Staff salaries, freelancers, mentors, trainers, consultants, delivery partners.

**2. Delivery Costs**
Equipment, materials, software, subscriptions, venue hire, travel, events.

**3. Operations**
Rent, utilities, insurance, admin, licences, basic marketing, overheads.

**4. Capital (if allowed)**
Larger one-off items: refurbishment, vehicles, machinery, significant IT.

**5. Contingency**
Typically 5–10%, if permitted by the funder's rules.

ROOTT helps you structure your budget into these categories and present it in a funder-friendly format.`
  },
  {
    id: "measurable_outcomes",
    title: "Measurable Outcomes (All Industries)",
    subtitle: "Funders invest in change, not just activity.",
    tags: ["all_industries", "grants"],
    body: `Activities describe what you do. Outcomes describe what changes.

Use strong verbs that funders recognise:
• Increase
• Reduce
• Improve
• Enable
• Support
• Strengthen
• Build
• Accelerate

**Example outcome statements:**
• "Increase access to training for 50 unemployed adults per year."
• "Improve mental health and confidence for young people aged 16–25."
• "Reduce carbon emissions for 20 local businesses by at least 10%."
• "Support 10 local artists to create and showcase new work."
• "Enable 30 startups to test and validate their ideas."

ROOTT converts your project description into clear, measurable outcome statements to match different funders' wording.`
  },
  {
    id: "evidence_of_need",
    title: "Evidence of Need",
    subtitle: "Prove that the problem you address is real.",
    tags: ["all_industries", "grants", "investors"],
    body: `Funders want to see that your work is based on real need, not just assumptions.

**Useful sources of evidence include:**
• Local or national statistics
• Community surveys or feedback
• Waiting lists and demand you cannot currently meet
• Case studies and stories from people you work with
• Market research or industry reports
• Feedback from partners, councils, schools, or other stakeholders
• Your own lived experience and professional insight

ROOTT can turn even rough notes into a structured, convincing "case for need" tailored to each application.`
  },
  {
    id: "reducing_risk",
    title: "Show Your Project Is Low Risk",
    subtitle: "Reassure funders that you can deliver.",
    tags: ["all_industries", "grants", "investors"],
    body: `Funders are more likely to support projects that feel safe, realistic, and well-managed.

**Across all sectors, it helps to show:**
• Experienced leadership or advisory support
• Clear timeline with milestones and responsibilities
• Partnerships with credible organisations or networks
• Safeguarding policies and checks where relevant (e.g. work with children or vulnerable adults)
• Backup delivery options, such as online or hybrid, if things change
• Realistic budget with no hidden surprises
• Evidence of past success, even on a small scale

ROOTT prompts you for these details and helps you frame them in a way that reassures funders.`
  },
  {
    id: "crowdfunding_basics",
    title: "Crowdfunding Success Basics",
    subtitle: "Simple rules that apply to any mission or sector.",
    tags: ["crowdfunding"],
    body: `Whether you're a charity, a social enterprise, a startup, or an individual, most successful crowdfunding campaigns share the same foundations:

**1. A strong one-line pitch**
A simple sentence that says who you are helping and how.
(ROOTT can generate this automatically.)

**2. A specific, meaningful target**
Don't set it unrealistically high. Choose an amount you can explain clearly.

**3. Relatable, human language**
Tell a real story. Avoid buzzwords and technical jargon.

**4. Regular updates**
Keep supporters informed and involved as you progress.

**5. Use your existing networks**
Social media, email lists, community groups, partners, friends, colleagues.

In ROOTT, your profile and project details can be repurposed into a campaign page, supporter updates, and matching social posts.`
  },
  {
    id: "funder_ready_check",
    title: "Funder-Ready Check",
    subtitle: "A quick checklist before you press submit.",
    tags: ["all_industries", "grants", "crowdfunding", "investors"],
    body: `Before you submit any application or campaign, run through this checklist:

• Have you clearly explained the need?
• Have you described a realistic plan?
• Is your budget transparent and aligned with your activities?
• Are your outcomes specific and measurable?
• Is your project a good fit with this funder's priorities?
• Have you shown why you are the right person or team to deliver it?
• Have you addressed risk, safeguarding, and inclusion where relevant?
• Have you answered each question directly, in the funder's own language?

ROOTT can analyse your draft and highlight gaps against these points, so you can improve your chances before submitting.`
  }
];

interface ToolkitCardProps {
  section: FundingSection;
  isOpen: boolean;
  onToggle: () => void;
}

const ToolkitCard: React.FC<ToolkitCardProps> = ({ section, isOpen, onToggle }) => {
  const formatBody = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h3 key={i} className="font-semibold text-gray-800 mt-4 mb-2">{line.replace(/\*\*/g, '')}</h3>;
      }
      if (line.startsWith('• ')) {
        return <li key={i} className="ml-4 text-[#777B7F]">{line.substring(2)}</li>;
      }
      if (line.trim() === '') {
        return <br key={i} />;
      }
      return <p key={i} className="text-[#777B7F] mb-2">{line}</p>;
    });
  };

  return (
    <div className="border rounded-lg overflow-hidden shadow-sm mb-3">
      <button
        onClick={onToggle}
        className="w-full flex justify-between items-center p-4 bg-white hover:bg-gray-50 transition"
      >
        <div className="text-left flex-1">
          <span className="text-gray-800 font-medium">{section.title}</span>
          {section.subtitle && (
            <p className="text-sm text-[#777B7F] mt-1">{section.subtitle}</p>
          )}
        </div>
        <span className="border px-0 flex items-center rounded-xl ml-4">
          {isOpen ? <MinusIcon /> : <PlusIcon />}
        </span>
      </button>
      
      {isOpen && (
        <div className="p-4 text-[#777B7F] bg-white border-t">
          <div className="space-y-1">
            {formatBody(section.body)}
          </div>
        </div>
      )}
    </div>
  );
};

const FundingToolkit: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleSection = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="border p-6 bg-[#F1F1F1] shadow-sm">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Funding Toolkit</h1>
        <p className="mt-2 text-[#777B7F]">
          Universal guidance for grants, crowdfunding, and investment — for all industries and missions.
        </p>
      </div>

      {/* Toolkit Sections */}
      <div className="space-y-3">
        {fundingContent.map((section, index) => (
          <ToolkitCard
            key={section.id}
            section={section}
            isOpen={openIndex === index}
            onToggle={() => toggleSection(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default FundingToolkit;